import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Lock,
  Shield,
  Key,
  Users,
  Settings,
  Plus,
  Trash2,
  Copy,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Info,
  Save,
  RefreshCw
} from 'lucide-react'
import { toast } from 'sonner'
import { serversApi } from '@/lib/api'

interface OAuth2AuthorizationConfig {
  enabled: boolean
  authorization_server: {
    issuer: string
    authorization_endpoint: string
    token_endpoint: string
    introspection_endpoint: string
    revocation_endpoint: string
    jwks_uri: string
    registration_endpoint: string
    scopes_supported: string[]
    response_types_supported: string[]
    grant_types_supported: string[]
    code_challenge_methods_supported: string[]
    token_endpoint_auth_methods_supported: string[]
  }
  resource_metadata: {
    resource: string
    authorization_servers: string[]
    scopes_supported: string[]
    bearer_methods_supported: string[]
  }
  dynamic_registration: {
    enabled: boolean
    initial_access_token_required: boolean
    registration_endpoint: string
  }
  registered_clients: Array<{
    client_id: string
    client_name: string
    client_uri?: string
    redirect_uris: string[]
    grant_types: string[]
    response_types: string[]
    scopes: string[]
    created_at: string
    status: 'active' | 'disabled' | 'revoked'
  }>
  access_token_ttl?: string
  refresh_token_ttl?: string
  authorization_code_ttl?: string
}

interface OAuth21ConfigurationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  serverId: string
  serverName: string
  port: number
  initialConfig?: OAuth2AuthorizationConfig
  onConfigUpdate?: (config: OAuth2AuthorizationConfig) => void
}

export function OAuth21ConfigurationModal({
  open,
  onOpenChange,
  serverId,
  serverName,
  port,
  initialConfig,
  onConfigUpdate
}: OAuth21ConfigurationModalProps) {
  const [config, setConfig] = useState<OAuth2AuthorizationConfig>({
    enabled: false,
    authorization_server: {
      issuer: `http://localhost:${port}`,
      authorization_endpoint: `http://localhost:${port}/oauth/authorize`,
      token_endpoint: `http://localhost:${port}/oauth/token`,
      introspection_endpoint: `http://localhost:${port}/oauth/introspect`,
      revocation_endpoint: `http://localhost:${port}/oauth/revoke`,
      jwks_uri: `http://localhost:${port}/.well-known/jwks.json`,
      registration_endpoint: `http://localhost:${port}/oauth/register`,
      scopes_supported: ['read', 'write', 'admin'],
      response_types_supported: ['code'],
      grant_types_supported: ['authorization_code', 'client_credentials', 'refresh_token'],
      code_challenge_methods_supported: ['S256'],
      token_endpoint_auth_methods_supported: ['client_secret_basic', 'client_secret_post', 'none']
    },
    resource_metadata: {
      resource: `http://localhost:${port}/mcp`,
      authorization_servers: [`http://localhost:${port}`],
      scopes_supported: ['read', 'write'],
      bearer_methods_supported: ['header', 'body']
    },
    dynamic_registration: {
      enabled: true,
      initial_access_token_required: false,
      registration_endpoint: `http://localhost:${port}/oauth/register`
    },
    registered_clients: [],
    access_token_ttl: '1h',
    refresh_token_ttl: '24h',
    authorization_code_ttl: '10m'
  })

  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState("general")
  const [newScope, setNewScope] = useState("")
  const [newGrantType, setNewGrantType] = useState("")
  const [newResponseType, setNewResponseType] = useState("")
  const [newAuthMethod, setNewAuthMethod] = useState("")

  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig)
    }
  }, [initialConfig])

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const handleSaveConfiguration = async () => {
    try {
      setLoading(true)
      await serversApi.updateOauthConfig(serverId, config)
      toast.success('OAuth 2.1 configuration updated successfully')
      onConfigUpdate?.(config)
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to update OAuth configuration:', error)
      toast.error('Failed to update OAuth 2.1 configuration')
    } finally {
      setLoading(false)
    }
  }

  const addScope = () => {
    if (newScope.trim() && !(config.authorization_server?.scopes_supported || []).includes(newScope.trim())) {
      setConfig(prev => ({
        ...prev,
        authorization_server: {
          ...prev.authorization_server,
          scopes_supported: [...(prev.authorization_server?.scopes_supported || []), newScope.trim()]
        }
      }))
      setNewScope("")
    }
  }

  const removeScope = (scope: string) => {
    setConfig(prev => ({
      ...prev,
      authorization_server: {
        ...prev.authorization_server,
        scopes_supported: (prev.authorization_server?.scopes_supported || []).filter(s => s !== scope)
      }
    }))
  }

  const addGrantType = () => {
    if (newGrantType && !(config.authorization_server?.grant_types_supported || []).includes(newGrantType)) {
      setConfig(prev => ({
        ...prev,
        authorization_server: {
          ...prev.authorization_server,
          grant_types_supported: [...(prev.authorization_server?.grant_types_supported || []), newGrantType]
        }
      }))
      setNewGrantType("")
    }
  }

  const removeGrantType = (grantType: string) => {
    setConfig(prev => ({
      ...prev,
      authorization_server: {
        ...prev.authorization_server,
        grant_types_supported: (prev.authorization_server?.grant_types_supported || []).filter(g => g !== grantType)
      }
    }))
  }

  const addResponseType = () => {
    if (newResponseType && !(config.authorization_server?.response_types_supported || []).includes(newResponseType)) {
      setConfig(prev => ({
        ...prev,
        authorization_server: {
          ...prev.authorization_server,
          response_types_supported: [...(prev.authorization_server?.response_types_supported || []), newResponseType]
        }
      }))
      setNewResponseType("")
    }
  }

  const removeResponseType = (responseType: string) => {
    setConfig(prev => ({
      ...prev,
      authorization_server: {
        ...prev.authorization_server,
        response_types_supported: (prev.authorization_server?.response_types_supported || []).filter(r => r !== responseType)
      }
    }))
  }

  const addAuthMethod = () => {
    if (newAuthMethod && !(config.authorization_server?.token_endpoint_auth_methods_supported || []).includes(newAuthMethod)) {
      setConfig(prev => ({
        ...prev,
        authorization_server: {
          ...prev.authorization_server,
          token_endpoint_auth_methods_supported: [...(prev.authorization_server?.token_endpoint_auth_methods_supported || []), newAuthMethod]
        }
      }))
      setNewAuthMethod("")
    }
  }

  const removeAuthMethod = (authMethod: string) => {
    setConfig(prev => ({
      ...prev,
      authorization_server: {
        ...prev.authorization_server,
        token_endpoint_auth_methods_supported: (prev.authorization_server?.token_endpoint_auth_methods_supported || []).filter(a => a !== authMethod)
      }
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Lock className="mr-2 h-5 w-5" />
            OAuth 2.1 Configuration - {serverName}
          </DialogTitle>
          <DialogDescription>
            Configure OAuth 2.1 authorization server settings for MCP client authentication.
            Changes are compliant with OAuth 2.1 security best practices and the MCP specification.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
            <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
            <TabsTrigger value="registration">Registration</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">OAuth 2.1 Authorization Server</h3>
                  <p className="text-sm text-muted-foreground">
                    Enable OAuth 2.1 authorization for this MCP server
                  </p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
                />
              </div>

              {config.enabled && (
                <div className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="issuer">Issuer URL</Label>
                      <Input
                        id="issuer"
                        value={config.authorization_server.issuer}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          authorization_server: {
                            ...prev.authorization_server,
                            issuer: e.target.value
                          }
                        }))}
                        placeholder="https://example.com"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        The issuer identifier for this authorization server
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="resource">Resource Identifier</Label>
                      <Input
                        id="resource"
                        value={config.resource_metadata.resource}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          resource_metadata: {
                            ...prev.resource_metadata,
                            resource: e.target.value
                          }
                        }))}
                        placeholder="https://example.com/api"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        The protected resource identifier
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Token Time-to-Live Configuration</Label>
                      <div className="grid gap-4 md:grid-cols-3 mt-2">
                        <div>
                          <Label htmlFor="access-ttl" className="text-sm">Access Token TTL</Label>
                          <Input
                            id="access-ttl"
                            value={config.access_token_ttl || '1h'}
                            onChange={(e) => setConfig(prev => ({ ...prev, access_token_ttl: e.target.value }))}
                            placeholder="1h"
                          />
                          <p className="text-xs text-muted-foreground mt-1">e.g., 1h, 30m, 2h</p>
                        </div>

                        <div>
                          <Label htmlFor="refresh-ttl" className="text-sm">Refresh Token TTL</Label>
                          <Input
                            id="refresh-ttl"
                            value={config.refresh_token_ttl || '24h'}
                            onChange={(e) => setConfig(prev => ({ ...prev, refresh_token_ttl: e.target.value }))}
                            placeholder="24h"
                          />
                          <p className="text-xs text-muted-foreground mt-1">e.g., 24h, 720h, 30d</p>
                        </div>

                        <div>
                          <Label htmlFor="code-ttl" className="text-sm">Authorization Code TTL</Label>
                          <Input
                            id="code-ttl"
                            value={config.authorization_code_ttl || '10m'}
                            onChange={(e) => setConfig(prev => ({ ...prev, authorization_code_ttl: e.target.value }))}
                            placeholder="10m"
                          />
                          <p className="text-xs text-muted-foreground mt-1">e.g., 10m, 5m, 15m</p>
                        </div>
                      </div>
                    </div>

                    <div className="border rounded-lg p-4 bg-blue-50">
                      <div className="flex items-start space-x-2">
                        <Info className="h-4 w-4 text-blue-500 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-blue-900">OAuth 2.1 Security Features</p>
                          <ul className="text-blue-800 mt-1 space-y-1">
                            <li>• PKCE (Proof Key for Code Exchange) is enforced for all authorization code flows</li>
                            <li>• Implicit grant flow is disabled for security (use authorization code with PKCE)</li>
                            <li>• Short-lived authorization codes (recommended: 10 minutes or less)</li>
                            <li>• Client authentication methods support modern security practices</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="endpoints" className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">OAuth 2.1 Endpoints</h3>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="auth-endpoint">Authorization Endpoint</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="auth-endpoint"
                      value={config.authorization_server.authorization_endpoint}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        authorization_server: {
                          ...prev.authorization_server,
                          authorization_endpoint: e.target.value
                        }
                      }))}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(config.authorization_server.authorization_endpoint)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="token-endpoint">Token Endpoint</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="token-endpoint"
                      value={config.authorization_server.token_endpoint}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        authorization_server: {
                          ...prev.authorization_server,
                          token_endpoint: e.target.value
                        }
                      }))}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(config.authorization_server.token_endpoint)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="introspect-endpoint">Token Introspection Endpoint</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="introspect-endpoint"
                      value={config.authorization_server.introspection_endpoint}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        authorization_server: {
                          ...prev.authorization_server,
                          introspection_endpoint: e.target.value
                        }
                      }))}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(config.authorization_server.introspection_endpoint)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="revoke-endpoint">Token Revocation Endpoint</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="revoke-endpoint"
                      value={config.authorization_server.revocation_endpoint}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        authorization_server: {
                          ...prev.authorization_server,
                          revocation_endpoint: e.target.value
                        }
                      }))}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(config.authorization_server.revocation_endpoint)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="jwks-uri">JWKS URI</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="jwks-uri"
                      value={config.authorization_server.jwks_uri}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        authorization_server: {
                          ...prev.authorization_server,
                          jwks_uri: e.target.value
                        }
                      }))}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(config.authorization_server.jwks_uri)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="register-endpoint">Client Registration Endpoint</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="register-endpoint"
                      value={config.authorization_server.registration_endpoint}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        authorization_server: {
                          ...prev.authorization_server,
                          registration_endpoint: e.target.value
                        }
                      }))}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(config.authorization_server.registration_endpoint)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-md font-medium">Well-known Discovery Endpoints</h4>
                <div className="grid gap-2">
                  <div>
                    <Label className="text-sm text-muted-foreground">Authorization Server Metadata</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        value={`${config.authorization_server.issuer}/.well-known/oauth-authorization-server`}
                        readOnly
                        className="text-sm"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(`${config.authorization_server.issuer}/.well-known/oauth-authorization-server`)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm text-muted-foreground">Protected Resource Metadata</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        value={`${config.authorization_server.issuer}/.well-known/oauth-protected-resource`}
                        readOnly
                        className="text-sm"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(`${config.authorization_server.issuer}/.well-known/oauth-protected-resource`)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="capabilities" className="space-y-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">Supported Scopes</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(config.authorization_server?.scopes_supported || []).map((scope) => (
                    <Badge key={scope} variant="secondary" className="flex items-center space-x-1">
                      <span>{scope}</span>
                      <button
                        onClick={() => removeScope(scope)}
                        className="ml-1 hover:text-red-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center space-x-2">
                  <Input
                    value={newScope}
                    onChange={(e) => setNewScope(e.target.value)}
                    placeholder="Enter new scope"
                    onKeyPress={(e) => e.key === 'Enter' && addScope()}
                  />
                  <Button onClick={addScope} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Grant Types</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(config.authorization_server?.grant_types_supported || []).map((grantType) => (
                    <Badge key={grantType} variant="outline" className="flex items-center space-x-1">
                      <span>{grantType}</span>
                      <button
                        onClick={() => removeGrantType(grantType)}
                        className="ml-1 hover:text-red-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center space-x-2">
                  <Select value={newGrantType} onValueChange={setNewGrantType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select grant type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="authorization_code">Authorization Code</SelectItem>
                      <SelectItem value="client_credentials">Client Credentials</SelectItem>
                      <SelectItem value="refresh_token">Refresh Token</SelectItem>
                      <SelectItem value="device_code">Device Code</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addGrantType} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Response Types</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(config.authorization_server?.response_types_supported || []).map((responseType) => (
                    <Badge key={responseType} variant="outline" className="flex items-center space-x-1">
                      <span>{responseType}</span>
                      <button
                        onClick={() => removeResponseType(responseType)}
                        className="ml-1 hover:text-red-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center space-x-2">
                  <Select value={newResponseType} onValueChange={setNewResponseType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select response type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="code">Code</SelectItem>
                      <SelectItem value="token">Token (not recommended for OAuth 2.1)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addResponseType} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Token Endpoint Authentication Methods</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(config.authorization_server?.token_endpoint_auth_methods_supported || []).map((authMethod) => (
                    <Badge key={authMethod} variant="secondary" className="flex items-center space-x-1">
                      <span>{authMethod}</span>
                      <button
                        onClick={() => removeAuthMethod(authMethod)}
                        className="ml-1 hover:text-red-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center space-x-2">
                  <Select value={newAuthMethod} onValueChange={setNewAuthMethod}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select auth method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client_secret_basic">Client Secret Basic</SelectItem>
                      <SelectItem value="client_secret_post">Client Secret Post</SelectItem>
                      <SelectItem value="client_secret_jwt">Client Secret JWT</SelectItem>
                      <SelectItem value="private_key_jwt">Private Key JWT</SelectItem>
                      <SelectItem value="none">None (Public clients)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addAuthMethod} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-green-50">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-green-900">PKCE Enforcement</p>
                    <p className="text-green-800 mt-1">
                      PKCE (Proof Key for Code Exchange) with S256 challenge method is always enforced for authorization code flows.
                      This is a security requirement in OAuth 2.1.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="registration" className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Dynamic Client Registration</h3>
                  <p className="text-sm text-muted-foreground">
                    Allow clients to register dynamically with the authorization server
                  </p>
                </div>
                <Switch
                  checked={config.dynamic_registration.enabled}
                  onCheckedChange={(checked) => setConfig(prev => ({
                    ...prev,
                    dynamic_registration: {
                      ...prev.dynamic_registration,
                      enabled: checked
                    }
                  }))}
                />
              </div>

              {config.dynamic_registration.enabled && (
                <>
                  <div>
                    <Label htmlFor="reg-endpoint">Registration Endpoint</Label>
                    <Input
                      id="reg-endpoint"
                      value={config.dynamic_registration.registration_endpoint}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        dynamic_registration: {
                          ...prev.dynamic_registration,
                          registration_endpoint: e.target.value
                        }
                      }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Require Initial Access Token</Label>
                      <p className="text-xs text-muted-foreground">
                        Require an initial access token for client registration
                      </p>
                    </div>
                    <Switch
                      checked={config.dynamic_registration.initial_access_token_required}
                      onCheckedChange={(checked) => setConfig(prev => ({
                        ...prev,
                        dynamic_registration: {
                          ...prev.dynamic_registration,
                          initial_access_token_required: checked
                        }
                      }))}
                    />
                  </div>
                </>
              )}

              <div>
                <h4 className="text-md font-medium mb-4">Registered Clients</h4>
                {(!config.registered_clients || config.registered_clients.length === 0) ? (
                  <p className="text-sm text-muted-foreground">No clients registered yet.</p>
                ) : (
                  <div className="space-y-2">
                    {config.registered_clients.map((client) => (
                      <div key={client.client_id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-medium">{client.client_name || 'Unnamed Client'}</h5>
                            <p className="text-sm text-muted-foreground">ID: {client.client_id}</p>
                          </div>
                          <Badge 
                            variant={client.status === 'active' ? 'default' : 
                                   client.status === 'disabled' ? 'secondary' : 'destructive'}
                          >
                            {client.status || 'unknown'}
                          </Badge>
                        </div>
                        <div className="mt-2 grid gap-2 text-sm">
                          <div>
                            <span className="font-medium">Grant Types: </span>
                            {(client.grant_types || []).join(', ')}
                          </div>
                          <div>
                            <span className="font-medium">Scopes: </span>
                            {(client.scopes || []).join(', ')}
                          </div>
                          <div>
                            <span className="font-medium">Redirect URIs: </span>
                            {(client.redirect_uris || []).length} configured
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="advanced" className="space-y-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">Bearer Token Methods</h3>
                <div className="flex flex-wrap gap-2">
                  {(config.resource_metadata?.bearer_methods_supported || []).map((method) => (
                    <Badge key={method} variant="outline">{method}</Badge>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Supported methods for including bearer tokens in API requests
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Resource Metadata</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="resource-scopes">Resource Scopes</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(config.resource_metadata?.scopes_supported || []).map((scope) => (
                        <Badge key={scope} variant="secondary">{scope}</Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="auth-servers">Authorization Servers</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(config.resource_metadata?.authorization_servers || []).map((server) => (
                        <Badge key={server} variant="outline">{server}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Configuration Export</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Full OAuth 2.1 Configuration (JSON)</Label>
                    <Textarea
                      value={JSON.stringify(config, null, 2)}
                      readOnly
                      className="font-mono text-xs mt-2"
                      rows={10}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={() => copyToClipboard(JSON.stringify(config, null, 2))}
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Copy Configuration
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium">References</h3>
                <div className="space-y-2 text-sm">
                  <a 
                    href="https://modelcontextprotocol.io/specification/draft/basic/authorization" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center underline text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    MCP Authorization Specification
                  </a>
                  <br />
                  <a 
                    href="https://datatracker.ietf.org/doc/html/draft-ietf-oauth-security-topics" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center underline text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    OAuth 2.1 Security Best Practices
                  </a>
                  <br />
                  <a 
                    href="https://datatracker.ietf.org/doc/html/rfc7636" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center underline text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    PKCE Specification (RFC 7636)
                  </a>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveConfiguration} disabled={loading}>
            {loading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Configuration
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
} 